import { Box, Grid, Typography, Divider, Radio, RadioGroup, FormControlLabel } from "@mui/material"
import { useSelector } from "react-redux"

import { markerConstants } from "../../constants/marker.constants"
import { RootState, store } from "../../store";
import { ActivityFilterType } from "../../store/event-map.store";
import { useEffectiveSettings, SETTING } from "../../hooks/use-effective-settings";

interface Props {
    isMultipleExists: boolean,
    isMultipleEvents: boolean
}

export const MapLegend: React.FC<Props> = ({ isMultipleExists, isMultipleEvents }) => {
    const { filter, activityFilter } = useSelector((state: RootState) => state.eventMap);
    const { getSetting } = useEffectiveSettings();
    const activityStatusFiltersEnabled = getSetting(SETTING.activityStatusFilters, true);

    const handleActivityFilterChange = (filterType: ActivityFilterType) => {
        store.dispatch.eventMap.setActivityFilter(filterType);
    };

    return (
        <div id = "legend" >
            <Box sx={{
                ml: 1.2,
                width: 180,
                background: 'white',
                opacity: 1,
                borderRadius: 0.4,
                p: 2
            }}>
                <Grid container flexDirection={'column'}>
                    {isMultipleEvents &&
                        <Grid
                        mb={1} 
                        justifyContent={'start'} 
                        alignContent={'center'} 
                        alignItems={'center'} 
                        item 
                        display={'flex'} 
                        flexDirection={'row'}
                        sx={{
                            opacity: isMultipleExists ? 1 : 0.5
                        }}
                    >
                        <img
                            src={markerConstants?.multiple}
                            style={{
                                height: 20,
                                width: 20,
                                marginRight: 5
                            }}
                        />
                        <Typography fontWeight={500}>
                            Multiple Events
                        </Typography>
                    </Grid>
                    }
                    <Grid
                        mb={1}
                        justifyContent={'start'}
                        alignContent={'center'}
                        alignItems={'center'}
                        item
                        display={'flex'}
                        flexDirection={'row'}
                        onClick={() => {
                            store.dispatch.eventMap.toggleFilter(
                                'devices',
                                !filter.devices
                            );
                        }}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        <img
                            src={markerConstants?.device}
                            style={{
                                height: 20,
                                width: 20,
                                marginRight: 5,
                                filter: filter.devices ? 'none' : 'grayscale(1) brightness(0.7) contrast(0)',
                                opacity: filter.devices ? 1 : 0.8
                            }}
                        />
                        <Typography fontWeight={500}>
                            POM User
                        </Typography>
                    </Grid>
                    <Grid 
                        mb={1} 
                        justifyContent={'start'} 
                        alignContent={'center'} 
                        alignItems={'center'} 
                        item 
                        display={'flex'} 
                        flexDirection={'row'}
                        onClick={() => {
                            store.dispatch.eventMap.toggleFilter(
                                'alerts',
                                !filter.alerts
                            );
                        }}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        <img
                            src={markerConstants?.alert}
                            style={{
                                height: 20,
                                width: 20,
                                marginRight: 5,
                                filter: filter.alerts ? 'none' : 'grayscale(1) brightness(0.7) contrast(0)',
                                opacity: filter.alerts ? 1 : 0.8
                            }}
                        />
                        <Typography fontWeight={500}>
                            Alert
                        </Typography>
                    </Grid>
                    <Grid
                        mb={1} 
                        justifyContent={'start'} 
                        alignContent={'center'} 
                        alignItems={'center'} 
                        item 
                        display={'flex'} 
                        flexDirection={'row'}
                        onClick={() => {
                            store.dispatch.eventMap.toggleFilter(
                                'reports',
                                !filter.reports
                            );
                        }}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        <img
                            src={markerConstants?.report}
                            style={{
                                height: 20,
                                width: 20,
                                marginRight: 5,
                                filter: filter.reports ? 'none' : 'grayscale(1) brightness(0.7) contrast(0)',
                                opacity: filter.reports ? 1 : 0.8
                            }}
                        />
                        <Typography fontWeight={500}>
                            Report
                        </Typography>
                    </Grid>
                    <Grid
                        mb={1}
                        justifyContent={'start'}
                        alignContent={'center'}
                        alignItems={'center'}
                        item
                        display={'flex'}
                        flexDirection={'row'}
                        onClick={() => {
                            store.dispatch.eventMap.toggleFilter(
                                'safetyReport',
                                !filter.safetyReport
                            );
                        }}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        <img
                            src={markerConstants?.safetyReport}
                            style={{
                                height: 20,
                                width: 20,
                                marginRight: 5,
                                filter: filter.safetyReport ? 'none' : 'grayscale(1) brightness(0.7) contrast(0)',
                                opacity: filter.safetyReport ? 1 : 0.8
                            }}
                        />
                        <Typography fontWeight={500}>
                            Safety Report
                        </Typography>
                    </Grid>
                    {/* Activity Status Filters - only show if feature is enabled */}
                    {activityStatusFiltersEnabled && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography fontWeight={600} fontSize={13} sx={{ mb: 0.5 }}>
                                User Activity
                            </Typography>
                            <RadioGroup
                                value={activityFilter}
                                onChange={(e) => handleActivityFilterChange(e.target.value as ActivityFilterType)}
                            >
                                <FormControlLabel
                                    value="activeNow"
                                    control={<Radio size="small" color="primary" />}
                                    label={
                                        <Typography fontWeight={500} fontSize={13}>
                                            Active Now
                                        </Typography>
                                    }
                                    sx={{ ml: -0.5, mb: -0.5 }}
                                />
                                <FormControlLabel
                                    value="activeLastHours"
                                    control={<Radio size="small" color="primary" />}
                                    label={
                                        <Typography fontWeight={500} fontSize={13}>
                                            Last 3 Hours
                                        </Typography>
                                    }
                                    sx={{ ml: -0.5, mb: -0.5 }}
                                />
                                <FormControlLabel
                                    value="activeLast24Hours"
                                    control={<Radio size="small" color="primary" />}
                                    label={
                                        <Typography fontWeight={500} fontSize={13}>
                                            Last 24 Hours
                                        </Typography>
                                    }
                                    sx={{ ml: -0.5 }}
                                />
                            </RadioGroup>
                        </>
                    )}
                </Grid>
            </Box>
        </div>
    )
}